import React from "react";
import { compose, withHandlers } from "recompose";
import { Control, actions, Fieldset } from "react-redux-form";
import {
  Col,
  Row,
  UncontrolledButtonDropdown,
  DropdownToggle,
  DropdownMenu,
} from "reactstrap";
import {
  Page,
  onMount,
  withLocalForm,
  FormGroup,
  withReload,
  SubmitButton,
  required,
} from "components/common";
import { withXeroErrors } from "components";
import { NotificationManager } from "react-notifications";
import Select from "react-select";
import { set, orderBy } from "lodash";
import deepPick from "deepPick";
import { Trans } from "react-i18next";
import { Link } from "react-router-dom";
import { formatCurrency } from "services/util";
import { withOrganisationContextConsumer } from "contexts/OrganisationContext";
import BackButton from "components/refactoring/BackButton";
import { useUserContext } from "hooks";

const ColMD = (props) => <Col xs={false} md={6} {...props} />;

const PurchaseDetails = ({ accounts, taxRates, dispatch, state }) => (
  <Fieldset model=".PurchaseDetails" className="nw-inventoryStyle">
    <Row>
      <Col>
        <FormGroup label="Unit Price" model=".UnitPrice">
          <Control.text model=".UnitPrice" type="number" step="any" />
        </FormGroup>
      </Col>
      <Col>
        {state.IsTrackedAsInventory ? (
          <FormGroup
            label="Cost of Goods Sold Account"
            model=".COGSAccountCode"
            addControlClass={false}
          >
            <Control.select
              component={Select}
              labelKey="Description"
              valueKey="Code"
              options={accounts}
              model=".COGSAccountCode"
              changeAction={(m, v) => {
                dispatch(actions.change(m, v ? v.Code : null));
                dispatch(
                  actions.change(
                    "local.PurchaseDetails.TaxType",
                    v ? v.TaxType : null
                  )
                );
              }}
            />
          </FormGroup>
        ) : (
          <FormGroup
            label="Purchases Account"
            model=".AccountCode"
            addControlClass={false}
          >
            <Control.select style={{ backgroundColor: '#F4F7FE' }}

              component={Select}
              labelKey="Description"
              valueKey="Code"
              options={accounts}
              model=".AccountCode"
              changeAction={(m, v) => {
                dispatch(actions.change(m, v ? v.Code : null));
                dispatch(
                  actions.change(
                    "local.PurchaseDetails.TaxType",
                    v ? v.TaxType : null
                  )
                );
              }}
            />
          </FormGroup>
        )}
      </Col>
      <Col>
        <FormGroup label="Tax Rate" model=".TaxType" addControlClass={false}>
          <Control
            component={Select}
            model=".TaxType"
            labelKey="Name"
            valueKey="TaxType"
            options={taxRates}
            simpleValue style={{ backgroundColor: '#F4F7FE' }}
          />
        </FormGroup>
      </Col>
    </Row>
    <FormGroup label="Purchases Description" className="pt-2 pb-2">
      <Control.textarea model="local.PurchaseDescription" style={{ backgroundColor: '#F4F7FE' }} />
    </FormGroup>
  </Fieldset>
);

const SalesDetails = ({ accounts, taxRates, dispatch }) => (
  <Fieldset model=".SalesDetails">
    <Row>
      <Col>
        <FormGroup label="Unit Price" model=".UnitPrice">
          <Control.text model=".UnitPrice" type="number" step="any" />
        </FormGroup>
      </Col>
      <Col>
        <FormGroup
          label="Sales Account"
          model=".AccountCode"
          addControlClass={false}
        >
          <Control.select style={{ backgroundColor: '#F4F7FE' }}
            component={Select}
            labelKey="Description"
            valueKey="Code"
            options={accounts}
            model=".AccountCode"
            changeAction={(m, v) => {
              dispatch(actions.change(m, v ? v.Code : null));
              dispatch(
                actions.change(
                  "local.SalesDetails.TaxType",
                  v ? v.TaxType : null
                )
              );
            }}
          />
        </FormGroup>
      </Col>
      <Col>
        <FormGroup label="Tax Rate" model=".TaxType" addControlClass={false}>
          <Control style={{ backgroundColor: '#F4F7FE' }}
            component={Select}
            model=".TaxType"
            labelKey="Name"
            valueKey="TaxType"
            options={taxRates}
            simpleValue
          />
        </FormGroup>
      </Col>
    </Row>
    <FormGroup label="Sales Description" className="pt-2 pb-2">
      <Control.textarea model="local.Description" style={{ backgroundColor: '#F4F7FE' }} />
    </FormGroup>
  </Fieldset>
);

export const EditInventory = ({
  state,
  handleSubmit,
  XeroErrors,
  dispatch,
  LocalForm,
  accounts,
  inventoryAccounts,
  taxRates,
  purchaseAccounts,
  match,
  item,
  inline,
  ...props
}) => {
  const Component = inline ? "div" : Page;
  const user = useUserContext();
  const can_view_cost = user?.permissions.includes('view_inventory_cost');
  return (
    <Component title={state.ItemID ? "Edit Item" : "New Item"}>
      <BackButton />
      <XeroErrors />
      <div className="d-flex justify-content-end">
        {!!item.IsTrackedAsInventory && (
          <UncontrolledButtonDropdown>
            <DropdownToggle caret color="primary" className="btn-sm">
              <Trans>Options</Trans>
            </DropdownToggle>
            <DropdownMenu>
              <Link className="dropdown-item" to={`${match.url}/adjustment`}>
                <Trans>Make adjustments</Trans>
              </Link>
            </DropdownMenu>
          </UncontrolledButtonDropdown>
        )}
      </div>
      <LocalForm
        onSubmit={handleSubmit}
        validators={{
          Code: { required },
          InventoryAssetAccountCode: (v) => {
            if (state.IsTrackedAsInventory) return v;
            return true;
          },
          "PurchaseDetails.COGSAccountCode": (v) => {
            if (state.IsTrackedAsInventory) return v;
            return true;
          },
          "PurchaseDetails.TaxType": (v) => {
            if (state.PurchaseDetails?.COGSAccountCode) return v;
            return true;
          },
          "SalesDetails.TaxType": (v) => {
            if (state.SalesDetails?.AccountCode) return v;
            return true;
          },
        }}
      >
        <Row>
          <ColMD>
            <FormGroup label="Item Code" model=".Code">
              <Control.text model=".Code" />
            </FormGroup>
          </ColMD>
          <ColMD>
            <FormGroup label="Item Name" model=".Name">
              <Control.text model=".Name" />
            </FormGroup>
          </ColMD>
        </Row>
        <hr />

        <Row>
          <ColMD style={{ 'color': "#72B566" }}>
            <FormGroup
              check
              label="I track this item"
              model=".IsTrackedAsInventory"
            >
              <Control.checkbox
                model=".IsTrackedAsInventory"
                changeAction={(m, v) => {
                  dispatch(actions.change(m, v));
                  dispatch(actions.omit("local", "InventoryAssetAccountCode"));

                  if (v) {
                    dispatch(
                      actions.omit("local.PurchaseDetails", "AccountCode")
                    );
                    dispatch(actions.change("local.IsSold", true));
                    dispatch(actions.change("local.IsPurchased", true));
                  } else {
                    dispatch(
                      actions.omit("local.PurchaseDetails", "COGSAccountCode")
                    );
                  }
                }}
              />
            </FormGroup>
          </ColMD>
        </Row>
        <Row>
          {!!state.IsTrackedAsInventory && (
            <ColMD>
              <FormGroup
                label="Inventory Asset Account"
                model=".InventoryAssetAccountCode"
                addControlClass={false}
              >
                <Control.select
                  options={accounts.inventory}
                  component={Select}
                  model=".InventoryAssetAccountCode"
                  labelKey="Description"
                  valueKey="Code"
                  simpleValue
                />
              </FormGroup>
            </ColMD>
          )}
        </Row>
        <hr />
        {can_view_cost &&
          <>
            <Row>
              <Col xs={false} md={3} style={{ 'color': "#72B566" }}>
                <FormGroup check label="I purchase this item" model=".IsPurchased">
                  <Control.checkbox
                    disabled={state.IsTrackedAsInventory}
                    model=".IsPurchased"
                    changeAction={(m, v) => {
                      dispatch(actions.change(m, v));
                      if (!v) {
                        dispatch(
                          actions.omit("local", [
                            "PurchaseDetails",
                            "PurchaseDescription",
                          ])
                        );
                      }
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>

            {!!state.IsPurchased && (
              <Row>
                <Col>
                  <PurchaseDetails
                    dispatch={dispatch}
                    accounts={accounts.payable}
                    taxRates={taxRates}
                    state={state}
                  />
                </Col>
              </Row>
            )}
          </>
        }

        <hr />
        <Row>
          <Col xs={false} md={3} style={{ 'color': "#72B566" }}>
            <FormGroup check label="I sell this item" model=".IsSold">
              <Control.checkbox
                model=".IsSold"
                disabled={state.IsTrackedAsInventory}
                changeAction={(m, v) => {
                  dispatch(actions.change(m, v));
                  if (!v) {
                    dispatch(
                      actions.omit("local", ["SalesDetails", "Description"])
                    );
                  }
                }}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          {!!state.IsSold && (
            <Col>
              <SalesDetails
                dispatch={dispatch}
                accounts={accounts.receivable}
                taxRates={taxRates}
              />
            </Col>
          )}
        </Row>
        {!!(state.ItemID && state.IsTrackedAsInventory) && (
          <div>
            <hr />
            <Row className="text-center">
              <Col>
                <FormGroup label="Quantity on Hand">
                  <div className="form-control-plaintext font-weight-bold">
                    {state.QuantityOnHand}
                  </div>
                </FormGroup>
              </Col>
              {can_view_cost &&
                <Col>
                  <FormGroup label="Average Cost">
                    <div className="form-control-plaintext font-weight-bold">
                      {formatCurrency(
                        state.QuantityOnHand
                          ? state.TotalCostPool / state.QuantityOnHand
                          : 0
                      )}
                    </div>
                  </FormGroup>
                </Col>
              }
              <Col>
                <FormGroup label="Total Value">
                  <div className="form-control-plaintext font-weight-bold">
                    {formatCurrency(state.TotalCostPool)}
                  </div>
                </FormGroup>
              </Col>
            </Row>
          </div>
        )}
        <div className="d-flex justify-content-end mt-3">
          <SubmitButton>Save</SubmitButton>
        </div>
      </LocalForm>
    </Component>
  );
};

export const enhance = compose(
  withOrganisationContextConsumer,
  withReload(),
  onMount(({ xero, match }) => () => {
    const { id } = match.params;

    return Promise.props({
      item:
        id === "new"
          ? {
            Code: "",
            Name: "",
            IsTrackedAsInventory: false,
            InventoryAssetAccountCode: null,
            IsSold: true,
            IsPurchased: true,
            Description: "",
            PurchaseDescription: "",
            PurchaseDetails: {},
            SalesDetails: {},
          }
          : xero.get(`items/${id}`).get("data"),
      taxRates: xero.get("taxrates").get("data"),
      accounts: xero
        .get("accounts")
        .get("data")
        .map((i) => set(i, "Description", `${i.Code} ${i.Name}`))
        .then((i) => orderBy(i, "Code"))
        .then((accounts) => ({
          receivable: accounts.filter((i) =>
            i.AllowReceivable === undefined ? true : i.AllowReceivable
          ),
          payable: accounts.filter((i) =>
            i.AllowPayable === undefined ? true : i.AllowPayable
          ),
          inventory: accounts.filter((i) => i.Type === "INVENTORY"),
        })),
    });
  }),
  withXeroErrors(),
  withLocalForm({
    initialState: (props) => props.item,
  }),
  withHandlers({
    handleSubmit:
      ({
        dispatch,
        xero,
        reload,
        setXeroErrors,
        t,
        history,
        clearContext,
        onSaved,
        inline,
      }) =>
        () => {
          dispatch(actions.setPending("local", true));
          dispatch((_, getState) => {
            const allowedProps = [
              "ItemID",
              "Code",
              "InventoryAssetAccountCode",
              "Name",
              "IsSold",
              "IsPurchased",
              "Description",
              "PurchaseDescription",
              "SalesDetails.UnitPrice",
              "SalesDetails.AccountCode",
              "SalesDetails.TaxType",
              "PurchaseDetails.UnitPrice",
              "PurchaseDetails.COGSAccountCode",
              "PurchaseDetails.TaxType",
              "PurchaseDetails.AccountCode"
            ];

            const local = getState().local;
            const payload = deepPick(local, allowedProps);

            if (!local.IsTrackedAsInventory) {
              payload.InventoryAssetAccountCode = undefined;
              if (payload.PurchaseDetail != null)
                payload.PurchaseDetails.COGSAccount = undefined;
            } else {
              payload.PurchaseDetails.AccountCode = undefined;
            }

            const { SalesDetails, PurchaseDetails } = payload;

            if (SalesDetails?.UnitPrice === "") SalesDetails.UnitPrice = null;
            if (PurchaseDetails?.UnitPrice === "")
              PurchaseDetails.UnitPrice = null;

            xero[payload.ItemID ? "put" : "post"]("items", payload)
              .then((resp) => {
                if (onSaved) onSaved(resp.data);
                NotificationManager.success(t("Saved successfully"));

                if (!inline) {
                  history.replace(resp.data.ItemID);
                }

                clearContext("items");
                reload();
              }, setXeroErrors)
              .finally(() => dispatch(actions.setPending("local", false)));
          });
        },
  })
);

export default enhance(EditInventory);
